import { Button } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { format } from 'date-fns';
import numeral from 'numeral';
import { useCallback, useEffect, useState } from 'react';
import { updateUserAccount } from '../api/auth';
import { getAllUsers } from '../api/super-admin';
import { OrganisationMemberDetails } from '../core/global';
import { userProfileShapeSuperAdmin } from '../data/form-shapes';
import styles from "../styles/components/super-admin-setting-users.module.scss";
import { CopyButton } from './copy-button';
import { ErrorMessage } from './error-message';
import { CreateForm } from './form-components';

export const SuperAdminSettingsUsers = () => {
  const [openEditUserId, setOpenEditUserId] = useState<null | number>(null)
  const [userDefaultValues, setUserDefaultValues] = useState<OrganisationMemberDetails | null>(null)

  const [allUsers, setAllUsers] = useState<OrganisationMemberDetails[]>([])
  const [apiError, setApiError] = useState<string[]>([])

  const fetchAllUsers = useCallback(async () => {
    const res = await getAllUsers()
    const {error, response, message} = res
    
    if (error === 200 && response) {
      const results = response?.results || []
      setAllUsers(results)
      setApiError([])
    } else {
      setApiError([message || "An error occurred while fetching your users."])
    }

  }, [])

  useEffect(() => {
    fetchAllUsers()
  }, [])

  useEffect(() => {
    // setUserDefaultValues(null)
    if (openEditUserId && allUsers.length > 0) {
      const found = allUsers.find(u => u.id === openEditUserId)
      setUserDefaultValues(found || null)
    } else {
      setUserDefaultValues(null)
    }
  }, [openEditUserId, allUsers])


  const columns: any = [
    {
      field: 'uid',
      headerName: 'ID',
      type: 'number',
      width: 80,
      editable: false,
    },
    {
      field: 'created_at',
      headerName: 'Created at',
      width: 150,
      editable: false,
      valueGetter: (params: any) => {
        return params ? new Date(params) : null;
      },
      valueFormatter: (params: any) => {
        return params ? format(params, 'dd/MM/yy') : 'N/A';
      },
      // sortComparator: (v1: Date, v2: Date) => {
      //   return (v1?.getTime?.() || 0) - (v2?.getTime?.() || 0);
      // },
    },
    {
      field: 'email',
      headerName: 'E-mail',
      width: 300,
      editable: false,
      renderCell: (params: any) => {
        return <span className={styles.email}>{params?.row?.email}<CopyButton content={params?.row?.email || ''} /></span>
      }
    },
    {
      field: 'last_login',
      headerName: 'Last login',
      width: 150,
      editable: false,
      valueGetter: (params: any) => {
        if (
          params &&
          Array.isArray(params) &&
          params.length > 0
        ) {
          return new Date(params[0]);
        }
      
        return null;
      },
      valueFormatter: (params: any) => {
        return params ? format(params, 'dd/MM/yy HH:mm') : 'N/A';
      },
    },
    {
      field: 'first_name',
      headerName: 'First Name',
      type: 'string',
      width: 180,
      editable: false,
    },
    {
      field: 'last_name',
      headerName: 'Last Name',
      type: 'string',
      width: 180,
      editable: false,
    },
    {
      field: 'is_confirmed',
      headerName: 'Confirmed',
      type: 'boolean',
      width: 80,
      editable: false,
    },
    {
      field: 'login_attempts',
      headerName: 'Login attempts',
      type: 'number',
      width: 80,
      editable: false,
      valueGetter: (params: any) => `${numeral(params?.value).format('0,0') || ''}`,
    },
    {
      headerName: 'Actions',
      width: 150,
      editable: false,
      renderCell: (params: any) => {
        return <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={() => {
            setOpenEditUserId(params?.row.id || null)
          }}
        >Edit</Button>
      }
    }
    
  ];

  return (
    <div>
      <ErrorMessage message={apiError} />
      <DataGrid
        rows={allUsers}
        columns={columns}
        initialState={{
          sorting: {
            sortModel: [{ field: 'created_at', sort: 'asc' }],
          },
          pagination: {
            paginationModel: {
              pageSize: 25,
            },
          },
        }}
        pageSizeOptions={[200]}
        disableRowSelectionOnClick
      />
      {openEditUserId && userDefaultValues &&
        <CreateForm
          onCancel={() => {
            setOpenEditUserId(null)
          }}
          title={'Edit user'}
          intro={'Edit user details below.'}
          size="medium"
          allowEdit={true}
          shape={userProfileShapeSuperAdmin}
          defaultValues={userDefaultValues}
          isModal
          onSuccess={() => {
            fetchAllUsers()
          }}
          onSubmit={(vals) => {
            const {first_name, last_name, email, notifications, enable_2fa} = vals
            return updateUserAccount({
              userId: openEditUserId,
              first_name, 
              last_name, 
              email, 
              notifications,
              enable_2fa
            })
          }}
        />
      }
    </div>
  )
}